import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { usePostGenerateCode } from '../../api'
import { ERROR_CODES } from '../../api/helpers/errorMatcher'
import { AlertType, useAlert } from '../../providers/alert'
import { NotFoundPage, ParentEmailConfirmationPage } from '../index'
import LoadingPage from '../LoadingPage'

enum VIEWS {
  LOADING = 'loading',
  NOT_FOUND = 'notFound',
  CONFIRMATION = 'confirmation',
}

/**
 * CodeController generates a one-time code for an approver to confirm their
 * email address, then redirects to the ParentEmailConfirmationPage to enter the
 * code. If the code generation fails because the account ID is not found, it
 * shows a NotFoundPage. If it fails for other reasons, it shows an alert.
 */
const CodeController: React.FC = () => {
  const { showAlert } = useAlert()

  const [id, setID] = useState<string>('')
  const { loading, data, error, callFetch, headers } = usePostGenerateCode(id)
  const [view, setView] = useState<VIEWS>(VIEWS.LOADING)

  const location = useLocation()
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    setID(params.get('id') || '')
  }, [location])

  useEffect(() => {
    if (id) {
      callFetch()
    }
  }, [id])

  useEffect(() => {
    if (loading) return
    if (error) {
      switch (error.code) {
        case ERROR_CODES.ERR_ACCOUNT_NOT_FOUND:
          setView(VIEWS.NOT_FOUND)
          break
        case ERROR_CODES.ERR_TOO_MANY_REQUESTS: // continue to parent email page
          setView(VIEWS.CONFIRMATION)
          break
        default:
          // TODO decide what we show the user if there was a network error or something
          // when trying to generate the code; for now it's this alert and loading page
          showAlert({
            msg: error.message,
            severity: AlertType.ERROR,
            duration: 5000,
          })
      }
    } else if (data) setView(VIEWS.CONFIRMATION)
  }, [loading, error, data])

  return (
    <>
      {view === VIEWS.LOADING && <LoadingPage />}
      {view === VIEWS.NOT_FOUND && <NotFoundPage />}
      {view === VIEWS.CONFIRMATION && (
        <ParentEmailConfirmationPage
          id={id}
          codeData={data}
          codeLoading={loading}
          codeError={error}
          codeCallFetch={callFetch}
          codeHeaders={headers}
        />
      )}
    </>
  )
}

export default CodeController
